import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { 
  investigateDataStatus, 
  findBackups, 
  recoverFromBackups,
  showStoreSummary,
  fullDataRecovery
} from '../utils/dataRecovery'

export default function DataDebug() {
  const [output, setOutput] = useState<string[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  
  const appendOutput = (text: string) => {
    setOutput(prev => [...prev, text])
  }
  
  const runInvestigation = async () => {
    setIsProcessing(true)
    setOutput([])
    
    try {
      appendOutput('📊 データ状態調査を開始...')
      const result = await investigateDataStatus()
      
      // LocalStorageデータ
      appendOutput('\n📦 LocalStorage内容:')
      Object.entries(result.localStorageData).forEach(([key, value]) => {
        if (key.includes('store') || key.includes('storage')) {
          const size = JSON.stringify(value).length
          appendOutput(`  ✅ ${key}: ${size} bytes`)
        }
      })
      
      // IndexedDBデータ
      if (result.indexedDBData) {
        appendOutput('\n🗄️ IndexedDB内容:')
        Object.entries(result.indexedDBData).forEach(([store, data]) => {
          appendOutput(`  📊 ${store}: ${data.length}件`)
        })
      }
    } catch (error) {
      appendOutput(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const runFindBackups = async () => {
    setIsProcessing(true)
    setOutput([])
    
    try {
      appendOutput('🔍 バックアップ検索を開始...')
      const backups = await findBackups()
      
      if (Object.keys(backups).length === 0) {
        appendOutput('バックアップが見つかりませんでした。')
      } else {
        appendOutput(`\n✅ ${Object.keys(backups).length}件のバックアップを発見:`)
        Object.keys(backups).forEach(key => {
          appendOutput(`  📦 ${key}`)
        })
      }
    } catch (error) {
      appendOutput(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const runRecovery = async () => {
    if (!confirm('バックアップからデータを復旧しますか？')) return
    
    setIsProcessing(true)
    setOutput([])
    
    try {
      appendOutput('♻️ データ復旧を開始...')
      const recovered = await recoverFromBackups()
      
      const successCount = Object.values(recovered).filter(v => v).length
      const failCount = Object.values(recovered).filter(v => !v).length
      
      appendOutput(`\n復旧結果:`)
      appendOutput(`  ✅ 成功: ${successCount}件`)
      appendOutput(`  ❌ 失敗: ${failCount}件`)
      
      Object.entries(recovered).forEach(([key, success]) => {
        appendOutput(`  ${success ? '✅' : '❌'} ${key}`)
      })
      
      if (successCount > 0) {
        appendOutput('\n⚠️ ページをリロードして変更を反映してください。')
      }
    } catch (error) {
      appendOutput(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const runStoreSummary = async () => {
    setIsProcessing(true)
    setOutput([])
    
    try {
      appendOutput('📈 ストア別サマリーを生成...')
      
      // コンソールの出力をキャプチャ
      const originalLog = console.log
      const logs: string[] = []
      console.log = (...args) => {
        logs.push(args.join(' '))
      }
      
      await showStoreSummary()
      
      console.log = originalLog
      
      logs.forEach(log => appendOutput(log))
    } catch (error) {
      appendOutput(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const runFullRecovery = async () => {
    setIsProcessing(true)
    setOutput([])
    
    try {
      appendOutput('🚀 完全データ復旧プロセスを開始...')
      
      const result = await fullDataRecovery()
      
      appendOutput('\n📊 診断結果:')
      appendOutput(`  LocalStorageキー数: ${Object.keys(result.status.localStorageData).length}`)
      appendOutput(`  バックアップ数: ${Object.keys(result.backups).length}`)
      appendOutput(`  ストアデータ: ${result.hasLocalData ? '有' : '無'}`)
      
      if (result.hasLocalData) {
        appendOutput('\n✅ LocalStorageにデータが存在します。')
        appendOutput('現在のデータは正常に使用可能です。')
      }
      
      if (Object.keys(result.backups).length > 0) {
        appendOutput('\n💡 バックアップからの復旧が可能です。')
        appendOutput('「バックアップから復旧」ボタンをクリックしてください。')
      }
    } catch (error) {
      appendOutput(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">データ復旧ツール</h1>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <p className="text-yellow-800">
          ⚠️ このツールはデータの調査と復旧を支援します。
          操作前に重要なデータのバックアップを取ることをお勧めします。
        </p>
      </div>
      
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          onClick={runFullRecovery}
          disabled={isProcessing}
          variant="default"
        >
          🚀 完全診断
        </Button>
        
        <Button
          onClick={runInvestigation}
          disabled={isProcessing}
          variant="secondary"
        >
          📊 データ状態調査
        </Button>
        
        <Button
          onClick={runStoreSummary}
          disabled={isProcessing}
          variant="secondary"
        >
          📈 ストアサマリー
        </Button>
        
        <Button
          onClick={runFindBackups}
          disabled={isProcessing}
          variant="secondary"
        >
          🔍 バックアップ検索
        </Button>
        
        <Button
          onClick={runRecovery}
          disabled={isProcessing}
          variant="destructive"
        >
          ♻️ バックアップから復旧
        </Button>
      </div>
      
      <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm">
        <div className="mb-2 text-gray-400">出力:</div>
        {output.length === 0 ? (
          <div className="text-gray-500">ボタンをクリックして診断を開始してください。</div>
        ) : (
          <div className="whitespace-pre-wrap">
            {output.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
      </div>
      
      {isProcessing && (
        <div className="mt-4 text-center text-gray-500">
          処理中...
        </div>
      )}
    </div>
  )
}